import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { CardModule } from 'primeng/card';
import { UserStore } from '../../../stores/user/user.state';
import { ButtonModule } from 'primeng/button';
import { DebugComponent } from '../../atoms/debug/debug.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { InputTextModule } from 'primeng/inputtext';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { SidebarModule } from 'primeng/sidebar';
import { TabMenuModule } from 'primeng/tabmenu';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { User } from '../../../models/user';
import { MessageService } from 'primeng/api';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BaseListComponent } from '../../organisms/base-list/base-list.component';
import { TableListActionsComponent } from '../table-list-actions/table-list-actions.component';
import { TableCaptionComponent } from '../table-list-caption/table-list-caption.component';
import { MaskComponent } from '../../organisms/mask/mask.component';
import { RoutingService } from '../../../services/routing.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { getState } from '@ngrx/signals';
import { MailingService } from '../../../services/mailing.service';
import { RoleStore } from '../../../stores/role/role.state';
import { WorkspaceStore } from '../../../stores/workspace/workspace.state';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CardModule,
    ButtonModule,
    DebugComponent,
    HeaderComponent,
    InputTextModule,
    NgIf,
    PaginatorModule,
    RouterOutlet,
    SidebarModule,
    TabMenuModule,
    TableModule,
    ToastModule,
    TableListActionsComponent,
    TableCaptionComponent,
    MaskComponent,
    TooltipModule,
    TagModule,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
})
export class UserListComponent
  extends BaseListComponent<User>
  implements OnInit
{
  readonly activeUserStore = inject(ActiveUserStore);
  readonly store = inject(UserStore);
  readonly roleStore = inject(RoleStore);
  readonly workspaceStore = inject(WorkspaceStore);
  override sidebarId = 'user-administration';
  @Input() communeId?: string;
  @Input() addRoute: any[] = [{ outlets: { bearbeiten: 'neu' } }];
  users: User[] = [];

  constructor(
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private mailingService: MailingService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    effect(() => {
      const userState = getState(this.store);
      if (userState.trigger?.indexOf('sendUserMail') > -1) {
        const userId = this.store.lastPersistedId();
        const user = this.store.getById(userId);

        if (user && !user.locked && user.firstLogin) {
          this.mailingService.sendNewUserMail(user).subscribe();
        } else {
          console.log('user locked - no mail sent');
        }
      }
      this.init();
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.init();
  }

  /**
   * Initializes the user data either by loading users associated with a specific commune
   * for super admin users or by loading all users as an array for other cases.
   *
   * @return {void} This method does not return a value.
   */
  init(): void {
    if (this.communeId && this.activeUserStore.isSuperAdmin()) {
      this.store.loadByCommune(this.communeId).then(users => {
        this.users = Object.values(users);
      });
    } else {
      this.users = this.store.asArray();
    }
  }

  switchUser(id: string) {
    this.activeUserStore.switchUser(id);
  }

  getWorkspaceTooltip(user: User): string {
    const ids = this.store.getWorkspaceIds(user);
    if (!ids.length) return 'Keine Arbeitsbereiche';
    return ids
      .map(id => this.workspaceStore.items()[id]?.name || id)
      .join(', ');
  }

  getRoleTooltip(user: User): string {
    if (!user.workspaces || !user.workspaces.length) return 'Keine Rollen';

    return user.workspaces
      .map(uw => {
        const workspaceName =
          this.workspaceStore.items()[uw.workspace]?.name || uw.workspace;
        const roleNames = uw.roles
          .map(roleId => this.roleStore.items()[roleId]?.name || roleId)
          .join(', ');
        return `${workspaceName}: ${roleNames}`;
      })
      .join('\n');
  }

  getWorkspaceCount(user: User): number {
    return this.store.getWorkspaceIds(user).length;
  }

  getRoleCount(user: User): number {
    return this.store.getRoleIds(user).length;
  }
}
